import { FC, useEffect } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import moment from 'moment';

import FormDate from '@components/FormDate/FormDate';
import FormTextField from '@components/FormTextField/FormTextField';
import useToggle from '@hooks/useToggle';
import useUnsavedChanges from '@hooks/useUnsavedChanges';

import GlobalMessagePreview from './GlobalMessagePreview/GlobalMessagePreview';
import { IGlobalMessageFormProps } from './GlobalMessageForm.interface';

import './style.scss';

const GlobalMessageForm: FC<IGlobalMessageFormProps> = ({
  form,
  initialValues,
  values,
  isEditing,
  handleSubmit,
  hasValidationErrors,
  submitting,
  onEdit,
  onDelete,
  onCancel,
}) => {
  const { t } = useTranslation();

  const minStartDate = moment().startOf('day');
  const minEndDate = values.startDate ? moment(values.startDate) : minStartDate;

  const [showPreview, openPreview, handleClose] = useToggle();

  useEffect(() => {
    if (!isEditing) {
      form.reset(initialValues);
      handleClose();
    }
  }, [isEditing, form, initialValues, handleClose]);

  useUnsavedChanges();

  return (
    <div className="global-message-form">
      <div className="global-message-form__fieldset">
        <Field
          component={FormTextField}
          className="global-message-form__message"
          label={t('admin.message')}
          name="message"
          disabled={!isEditing}
          multiline
          required
        />

        <Field
          component={FormDate}
          className="global-message-form__date"
          label={t('admin.startDate')}
          disabled={!isEditing}
          minDate={isEditing ? minStartDate : undefined}
          name="startDate"
          required
        />

        <Field
          component={FormDate}
          className="global-message-form__date"
          label={t('admin.endDate')}
          disabled={!isEditing}
          minDate={isEditing ? minEndDate : undefined}
          name="endDate"
          required
        />
      </div>

      {showPreview && (
        <GlobalMessagePreview
          className="program-code-details-form__preview"
          title={t('admin.headlineText')}
          content={values.message}
        />
      )}

      {isEditing ? (
        <div className="global-message-form__controls">
          <Button variant="outlined" onClick={onCancel}>
            {t('admin.cancel')}
          </Button>

          {!showPreview && values.message && (
            <Button variant="outlined" color="primary" onClick={openPreview}>
              {t('admin.preview')}
            </Button>
          )}

          <Button
            variant="contained"
            disabled={hasValidationErrors || submitting}
            onClick={handleSubmit}
          >
            {t('admin.save')}
          </Button>
        </div>
      ) : (
        <div className="global-message-form__controls">
          <Button variant="outlined" onClick={onEdit}>
            {t('admin.edit')}
          </Button>

          <Button variant="outlined" color="error" onClick={onDelete}>
            {t('admin.delete')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GlobalMessageForm;
